import { useMutation, useQueryClient } from "@tanstack/react-query";
import { boardApi, type CreateStickyNotePayload } from "../../api/board";
import { boardKeys } from "../queries/useBoardNotes";
import type { StickyNote } from "../../types/domain";

export const useCreateNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStickyNotePayload) => boardApi.createNote(payload),
    onSuccess: (note) => {
      queryClient.setQueryData<StickyNote[]>(boardKeys.notes, (old) => {
        if (!old) return [note];
        if (old.some((n) => n.id === note.id)) return old;
        return [...old, note];
      });
    },
  });
};

export const useMoveNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, x, y, rotation }: { id: number; x: number; y: number; rotation?: number }) =>
      boardApi.moveNote(id, x, y, rotation),
    // Optimistic: the note already follows the cursor while dragging/rotating, so commit
    // the change locally right away and roll back only if the server rejects it.
    onMutate: async ({ id, x, y, rotation }) => {
      await queryClient.cancelQueries({ queryKey: boardKeys.notes });
      const snapshot = queryClient.getQueryData<StickyNote[]>(boardKeys.notes);
      queryClient.setQueryData<StickyNote[]>(boardKeys.notes, (old) =>
        old?.map((n) => (n.id === id ? { ...n, x, y, rotation: rotation ?? n.rotation } : n)),
      );
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(boardKeys.notes, ctx.snapshot);
    },
  });
};

export const useVoteNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // vote === null retracts the current user's vote
    mutationFn: ({ id, vote }: { id: number; vote: "YES" | "NO" | null }) =>
      vote ? boardApi.vote(id, vote) : boardApi.removeVote(id),
    onSuccess: (note) => {
      queryClient.setQueryData<StickyNote[]>(boardKeys.notes, (old) =>
        old?.map((n) => (n.id === note.id ? note : n)),
      );
    },
  });
};

export const useDeleteNote = (isAdminDelete: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => (isAdminDelete ? boardApi.deleteAsAdmin(id) : boardApi.deleteOwn(id)),
    onSuccess: (_res, id) => {
      queryClient.setQueryData<StickyNote[]>(boardKeys.notes, (old) =>
        old?.filter((n) => n.id !== id),
      );
    },
  });
};
